import React, { createContext, useState, useContext, useEffect } from 'react';
import { getStorageData, setStorageData } from '../utils/localStorage';

const AppContext = createContext();

export const useApp = () => {
  const context = useContext(AppContext);
  if (!context) {
    throw new Error('useApp must be used within AppProvider');
  }
  return context;
};

export const AppProvider = ({ children }) => {
  const [appData, setAppData] = useState(() => getStorageData());

  // Sync to localStorage whenever appData changes (debounced)
  useEffect(() => {
    const timeoutId = setTimeout(() => {
      setStorageData(appData);
    }, 100);
    
    return () => clearTimeout(timeoutId);
  }, [appData]);

  // Apply dark mode when preference changes
  useEffect(() => {
    const isDark = appData.preferences.darkMode;
    const hasDarkClass = document.documentElement.classList.contains('dark');
    
    if (isDark && !hasDarkClass) {
      document.documentElement.classList.add('dark');
    } else if (!isDark && hasDarkClass) {
      document.documentElement.classList.remove('dark');
    }
  }, [appData.preferences.darkMode]);

  // Documents
  const updateDocument = (documentId, updates) => {
    setAppData(prev => ({
      ...prev,
      documents: {
        ...prev.documents,
        [documentId]: {
          ...prev.documents[documentId],
          ...updates,
          updatedAt: new Date().toISOString()
        }
      }
    }));
  };

  const getDocument = (documentId) => {
    return appData.documents[documentId] || null;
  };

  // Notes
  const addNote = (pageId, content) => {
    const noteId = Date.now().toString();
    setAppData(prev => ({
      ...prev,
      notes: {
        ...prev.notes,
        [pageId]: [
          ...(prev.notes[pageId] || []),
          {
            id: noteId,
            content,
            createdAt: new Date().toISOString(),
            updatedAt: new Date().toISOString(),
            important: false
          }
        ]
      }
    }));
    return noteId;
  };

  const updateNote = (pageId, noteId, updates) => {
    setAppData(prev => ({
      ...prev,
      notes: {
        ...prev.notes,
        [pageId]: prev.notes[pageId]?.map(note =>
          note.id === noteId
            ? { ...note, ...updates, updatedAt: new Date().toISOString() }
            : note
        ) || []
      }
    }));
  };

  const deleteNote = (pageId, noteId) => {
    setAppData(prev => ({
      ...prev,
      notes: {
        ...prev.notes,
        [pageId]: prev.notes[pageId]?.filter(note => note.id !== noteId) || []
      }
    }));
  };

  const getNotes = (pageId) => {
    return appData.notes[pageId] || [];
  };

  // Wizards
  const updateWizardProgress = (wizardId, stepIndex, completed = false) => {
    setAppData(prev => ({
      ...prev,
      wizards: {
        ...prev.wizards,
        [wizardId]: {
          ...prev.wizards[wizardId],
          currentStep: stepIndex,
          completed,
          updatedAt: new Date().toISOString()
        }
      }
    }));
  };

  const getWizardProgress = (wizardId) => {
    return appData.wizards[wizardId] || { currentStep: 0, completed: false };
  };

  // Preferences
  const updatePreferences = (updates) => {
    setAppData(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        ...updates
      }
    }));
  };

  // Search history
  const addSearchHistory = (query) => {
    setAppData(prev => ({
      ...prev,
      searchHistory: [
        { query, timestamp: new Date().toISOString() },
        ...prev.searchHistory.filter(item => item.query !== query).slice(0, 9)
      ]
    }));
  };

  // Last visited
  const addLastVisited = (pageId, pageTitle) => {
    setAppData(prev => ({
      ...prev,
      lastVisited: [
        { pageId, pageTitle, timestamp: new Date().toISOString() },
        ...prev.lastVisited.filter(item => item.pageId !== pageId).slice(0, 9)
      ]
    }));
  };

  const value = {
    appData,
    // Documents
    updateDocument,
    getDocument,
    documents: appData.documents,
    // Notes
    addNote,
    updateNote,
    deleteNote,
    getNotes,
    notes: appData.notes,
    // Wizards
    updateWizardProgress,
    getWizardProgress,
    wizards: appData.wizards,
    // Preferences
    updatePreferences,
    preferences: appData.preferences,
    // Search
    addSearchHistory,
    searchHistory: appData.searchHistory,
    // Last visited
    addLastVisited,
    lastVisited: appData.lastVisited
  };

  return <AppContext.Provider value={value}>{children}</AppContext.Provider>;
};
